import '../../../../components/branding/SoriginLogoBanner.css';

export interface EpcSiteConstructionHeroProps {
  className?: string;
}

const LOCAL_STYLE = `
  .eb-epc-jib-sway {
    transform-origin: 0 0;
    animation: eb-epc-jib-sway 7s ease-in-out infinite;
    will-change: transform;
  }
  @keyframes eb-epc-jib-sway {
    0%   { transform: rotate(-1.2deg); }
    50%  { transform: rotate(1.2deg); }
    100% { transform: rotate(-1.2deg); }
  }
  .eb-epc-sun-spin {
    transform-origin: 0 0;
    animation: eb-epc-sun-spin 24s linear infinite;
    will-change: transform;
  }
  @keyframes eb-epc-sun-spin {
    from { transform: rotate(0deg); }
    to   { transform: rotate(360deg); }
  }
  .eb-epc-spark-pulse {
    animation: eb-epc-spark-pulse 2.4s ease-in-out infinite;
    will-change: opacity;
  }
  @keyframes eb-epc-spark-pulse {
    0%, 100% { opacity: 0.45; }
    50%      { opacity: 1; }
  }
  /* Wind-turbine blade rotation (geometry centred at 0,0 → pivots on hub) */
  .eb-epc-blades {
    transform-origin: 0 0;
    transform-box: fill-box;
    animation: eb-epc-blades 6s linear infinite;
    will-change: transform;
  }
  .eb-epc-blades-slow {
    transform-origin: 0 0;
    transform-box: fill-box;
    animation: eb-epc-blades 11s linear infinite;
    will-change: transform;
  }
  @keyframes eb-epc-blades {
    from { transform: rotate(0deg); }
    to   { transform: rotate(360deg); }
  }
  /* Energy flowing along the engraved transmission lines */
  .eb-epc-flow {
    stroke-dasharray: 3 5;
    animation: eb-epc-flow 1.6s linear infinite;
    will-change: stroke-dashoffset;
  }
  @keyframes eb-epc-flow {
    to { stroke-dashoffset: -16; }
  }
  @media (prefers-reduced-motion: reduce) {
    .eb-epc-jib-sway,
    .eb-epc-sun-spin,
    .eb-epc-spark-pulse,
    .eb-epc-blades,
    .eb-epc-blades-slow,
    .eb-epc-flow {
      animation: none !important;
    }
  }
`;

/**
 * Engraved wind turbine, hub-centred at (0,0). Pole drops to the ground,
 * blades spin around the hub. Used at several scales to build a wind farm.
 */
function EngravedTurbine({
  x,
  y,
  scale = 1,
  bladeClass = 'eb-epc-blades',
  opacity = 1,
}: {
  x: number;
  y: number;
  scale?: number;
  bladeClass?: string;
  opacity?: number;
}) {
  return (
    <g transform={`translate(${x} ${y}) scale(${scale})`} opacity={opacity}>
      {/* contact shadow at pole base */}
      <ellipse cx="0" cy="54" rx="9" ry="1.2" fill="#006346" fillOpacity="0.20" />
      {/* tapered pole */}
      <path d="M -1.8 54 L -0.5 0 L 0.5 0 L 1.8 54 Z" fill="url(#eb-epc-pole)" />
      {/* nacelle housing behind hub */}
      <path d="M -3.6 -1.4 L 0 -6 L 3.6 -1.4 Z" fill="#006346" fillOpacity="0.6" />
      <rect x="-2.4" y="-3.2" width="4.8" height="3" rx="0.6" fill="#006346" fillOpacity="0.55" />
      {/* spinning blades */}
      <g className={bladeClass}>
        <path d="M 2.4 0 Q 11 -1.4 21 -0.6 Q 27 0 21 0.6 Q 11 1.4 2.4 0 Z" fill="#006346" fillOpacity="0.72" />
        <path d="M 2.4 0 Q 11 -1.4 21 -0.6 Q 27 0 21 0.6 Q 11 1.4 2.4 0 Z" fill="#006346" fillOpacity="0.72" transform="rotate(120)" />
        <path d="M 2.4 0 Q 11 -1.4 21 -0.6 Q 27 0 21 0.6 Q 11 1.4 2.4 0 Z" fill="#006346" fillOpacity="0.72" transform="rotate(240)" />
      </g>
      {/* hub cap */}
      <circle r="2.6" fill="#A6CE39" />
      <circle r="2.6" fill="none" stroke="#006346" strokeOpacity="0.85" strokeWidth="0.5" />
      <circle r="0.9" fill="#006346" />
    </g>
  );
}

export default function EpcSiteConstructionHero({ className }: EpcSiteConstructionHeroProps) {
  return (
    <svg
      viewBox="0 0 480 120"
      preserveAspectRatio="xMidYMid slice"
      role="img"
      aria-hidden="true"
      className={`sorigin-banner ${className ?? ''}`}
    >
      <style>{LOCAL_STYLE}</style>

      <defs>
        <linearGradient id="eb-epc-tint" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#FBFEF9" />
          <stop offset="100%" stopColor="#F2F8EC" />
        </linearGradient>

        <linearGradient id="eb-epc-pole" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#006346" stopOpacity="0.72" />
          <stop offset="100%" stopColor="#076333" stopOpacity="0.55" />
        </linearGradient>

        <pattern
          id="eb-epc-microline"
          x="0"
          y="0"
          width="6"
          height="6"
          patternUnits="userSpaceOnUse"
          patternTransform="rotate(45)"
        >
          <line x1="0" y1="0" x2="0" y2="6" stroke="#006346" strokeOpacity="0.05" strokeWidth="0.4" />
        </pattern>
      </defs>

      {/* Layer 1 — parchment tint */}
      <rect width="480" height="120" fill="url(#eb-epc-tint)" />

      {/* Layer 2 — diagonal micro-line hatch */}
      <rect width="480" height="120" fill="url(#eb-epc-microline)" />

      {/* Layer 3 — banknote frame */}
      <rect x="5" y="5" width="470" height="110" rx="2" fill="none" stroke="#006346" strokeOpacity="0.22" strokeWidth="0.8" />
      <rect x="10" y="10" width="460" height="100" rx="1" fill="none" stroke="#52BF9D" strokeOpacity="0.32" strokeWidth="0.45" strokeDasharray="2 2.5" />

      {/* Layer 4 — corner flourishes + lime dots */}
      <g fill="none" stroke="#006346" strokeOpacity="0.55" strokeWidth="0.9" strokeLinecap="round">
        <path d="M 5 18 Q 5 5 18 5" />
        <path d="M 462 5 Q 475 5 475 18" />
        <path d="M 475 102 Q 475 115 462 115" />
        <path d="M 18 115 Q 5 115 5 102" />
      </g>
      <g fill="#A6CE39" fillOpacity="0.85">
        <circle cx="11" cy="11" r="1.4" />
        <circle cx="469" cy="11" r="1.4" />
        <circle cx="469" cy="109" r="1.4" />
        <circle cx="11" cy="109" r="1.4" />
      </g>

      {/* Layer 5 — left rosette medallion (identical to SoriginLogoBanner) */}
      <g transform="translate(60, 60)" fill="none" stroke="#006346" strokeWidth="0.5">
        <circle r="44" strokeOpacity="0.12" />
        <circle r="38" strokeOpacity="0.18" strokeDasharray="0.6 2" />
        <g strokeOpacity="0.10">
          <ellipse rx="34" ry="20" />
          <ellipse rx="34" ry="20" transform="rotate(30)" />
          <ellipse rx="34" ry="20" transform="rotate(60)" />
          <ellipse rx="34" ry="20" transform="rotate(90)" />
          <ellipse rx="34" ry="20" transform="rotate(120)" />
          <ellipse rx="34" ry="20" transform="rotate(150)" />
        </g>
        <circle r="26" strokeOpacity="0.16" />
        <circle r="14" strokeOpacity="0.10" />
      </g>

      {/* Layer 6 — engraved sun upper-right (behind crane silhouette) */}
      <g transform="translate(420, 28)">
        <g className="eb-epc-sun-spin">
          {/* 6 thin rays — drawn centered at (0,0) */}
          <g stroke="#006346" strokeOpacity="0.7" strokeWidth="0.5" strokeLinecap="round">
            <line x1="0" y1="-12" x2="0" y2="-18" />
            <line x1="0" y1="12" x2="0" y2="18" />
            <line x1="-12" y1="0" x2="-18" y2="0" />
            <line x1="12" y1="0" x2="18" y2="0" />
            <line x1="-8.5" y1="-8.5" x2="-12.7" y2="-12.7" />
            <line x1="8.5" y1="-8.5" x2="12.7" y2="-12.7" />
            <line x1="-8.5" y1="8.5" x2="-12.7" y2="12.7" />
            <line x1="8.5" y1="8.5" x2="12.7" y2="12.7" />
          </g>
        </g>
        {/* static concentric circles */}
        <circle r="9" fill="none" stroke="#A6CE39" strokeOpacity="0.7" strokeWidth="0.6" />
        <circle r="6" fill="none" stroke="#006346" strokeOpacity="0.55" strokeWidth="0.5" />
        <circle r="3" fill="#A6CE39" fillOpacity="0.45" />
      </g>

      {/* Layer 6b — engraved sky energy arcs (transmission flow across the top) */}
      <g fill="none" stroke="#006346" strokeLinecap="round">
        <path d="M 70 26 Q 230 6 410 26" strokeOpacity="0.16" strokeWidth="0.5" />
        <path d="M 70 26 Q 230 6 410 26" className="eb-epc-flow" stroke="#A6CE39" strokeOpacity="0.7" strokeWidth="0.6" />
        <path d="M 96 38 Q 250 20 404 40" strokeOpacity="0.10" strokeWidth="0.4" strokeDasharray="1.5 3" />
      </g>

      {/* Layer 6c — engraved wind farm (background, behind the build) */}
      <EngravedTurbine x={86} y={54} scale={0.6} bladeClass="eb-epc-blades-slow" opacity={0.5} />
      <EngravedTurbine x={158} y={50} scale={0.68} bladeClass="eb-epc-blades-slow" opacity={0.58} />

      {/* Layer 6d — PROMINENT foreground wind turbine (the wind hero, fills the empty sky) */}
      <g>
        {/* faint ground hatching under the hero turbine */}
        <g stroke="#006346" strokeOpacity="0.30" strokeWidth="0.5" strokeLinecap="round">
          <line x1="108" y1="103" x2="140" y2="103" strokeDasharray="2 3" />
          <line x1="112" y1="106" x2="136" y2="106" strokeDasharray="1 2" />
        </g>
        <EngravedTurbine x={124} y={44} scale={1.08} bladeClass="eb-epc-blades" opacity={0.95} />
        {/* small champagne sparkle off the blade tip */}
        <circle cx="150" cy="30" r="0.9" fill="#A6CE39" className="eb-epc-spark-pulse" fillOpacity="0.8" />
      </g>

      {/* Layer 7 — engraved tower crane (center focal) */}
      <g>
        {/* Vertical lattice mast: 4 thin parallel verticals at x=176,179,182,185 from y=30 to y=100 */}
        <g stroke="#006346" strokeWidth="0.5" strokeLinecap="round">
          <line x1="176" y1="30" x2="176" y2="100" strokeOpacity="0.7" />
          <line x1="179" y1="30" x2="179" y2="100" strokeOpacity="0.55" />
          <line x1="182" y1="30" x2="182" y2="100" strokeOpacity="0.55" />
          <line x1="185" y1="30" x2="185" y2="100" strokeOpacity="0.7" />
        </g>
        {/* Horizontal cross-braces — X pattern */}
        <g stroke="#006346" strokeOpacity="0.5" strokeWidth="0.45">
          {[36, 48, 60, 72, 84, 96].map((y, i) => (
            <g key={i}>
              <line x1="176" y1={y} x2="185" y2={y} />
              <line x1="176" y1={y} x2="185" y2={y + 6} />
              <line x1="185" y1={y} x2="176" y2={y + 6} />
            </g>
          ))}
        </g>
        {/* Mast base hatching */}
        <g stroke="#006346" strokeOpacity="0.35" strokeWidth="0.5" strokeLinecap="round">
          <line x1="170" y1="103" x2="191" y2="103" />
          <line x1="167" y1="106" x2="194" y2="106" strokeDasharray="1 2" />
        </g>

        {/* Jib group — pivot at mast top (180.5, 34) */}
        <g transform="translate(180.5, 34)">
          <g className="eb-epc-jib-sway">
            {/* Counterweight on left side */}
            <rect x="-22" y="-4" width="10" height="6" fill="none" stroke="#006346" strokeOpacity="0.75" strokeWidth="0.6" />
            <line x1="-22" y1="-1" x2="-12" y2="-1" stroke="#006346" strokeOpacity="0.5" strokeWidth="0.4" />
            {/* Counterweight tie-back diagonal */}
            <line x1="-12" y1="-4" x2="0" y2="-10" stroke="#076333" strokeOpacity="0.6" strokeWidth="0.5" />

            {/* Horizontal jib arm extending right — thin tapered */}
            <line x1="-12" y1="0" x2="80" y2="0" stroke="#076333" strokeOpacity="0.85" strokeWidth="0.7" />
            <line x1="-12" y1="2" x2="80" y2="2" stroke="#006346" strokeOpacity="0.55" strokeWidth="0.4" />

            {/* Apex tower above pivot + diagonal support cables to jib */}
            <line x1="0" y1="0" x2="0" y2="-12" stroke="#006346" strokeOpacity="0.75" strokeWidth="0.55" />
            <line x1="0" y1="-12" x2="78" y2="0" stroke="#006346" strokeOpacity="0.65" strokeWidth="0.45" />
            <line x1="0" y1="-12" x2="40" y2="0" stroke="#006346" strokeOpacity="0.55" strokeWidth="0.4" strokeDasharray="1 1.5" />
            <line x1="0" y1="-12" x2="-22" y2="-1" stroke="#006346" strokeOpacity="0.55" strokeWidth="0.4" strokeDasharray="1 1.5" />
            {/* Apex cap */}
            <circle cx="0" cy="-12" r="0.9" fill="#A6CE39" />

            {/* Trolley + cable hanging from jib end */}
            <rect x="74" y="-1.5" width="5" height="2.5" fill="none" stroke="#006346" strokeOpacity="0.7" strokeWidth="0.5" />
            <line x1="76.5" y1="1.2" x2="76.5" y2="22" stroke="#006346" strokeOpacity="0.7" strokeWidth="0.5" />

            {/* Hook */}
            <path d="M 75 22 Q 76.5 25.5 78 22" fill="none" stroke="#006346" strokeOpacity="0.8" strokeWidth="0.6" strokeLinecap="round" />

            {/* Cargo block — panel pallet (3 stacked horizontal lines as engraved rectangle) */}
            <rect x="69" y="25" width="15" height="9" fill="none" stroke="#006346" strokeOpacity="0.75" strokeWidth="0.55" />
            <line x1="69" y1="28" x2="84" y2="28" stroke="#006346" strokeOpacity="0.55" strokeWidth="0.4" />
            <line x1="69" y1="31" x2="84" y2="31" stroke="#006346" strokeOpacity="0.55" strokeWidth="0.4" />
            <line x1="76.5" y1="22" x2="76.5" y2="25" stroke="#006346" strokeOpacity="0.7" strokeWidth="0.45" />
          </g>
        </g>
      </g>

      {/* Layer 8 — engraved scaffold structure (right of crane) */}
      <g>
        {/* 4 vertical posts */}
        <g stroke="#006346" strokeOpacity="0.5" strokeWidth="0.5">
          <line x1="248" y1="60" x2="248" y2="100" />
          <line x1="266" y1="60" x2="266" y2="100" />
          <line x1="284" y1="60" x2="284" y2="100" />
          <line x1="302" y1="60" x2="302" y2="100" />
        </g>
        {/* 4 horizontal levels */}
        <g stroke="#006346" strokeOpacity="0.5" strokeWidth="0.5">
          <line x1="246" y1="62" x2="304" y2="62" />
          <line x1="246" y1="74" x2="304" y2="74" />
          <line x1="246" y1="86" x2="304" y2="86" />
          <line x1="246" y1="98" x2="304" y2="98" strokeDasharray="1 1.5" />
        </g>
        {/* Diagonal X-bracing across two bays */}
        <g stroke="#006346" strokeOpacity="0.32" strokeWidth="0.4">
          <line x1="248" y1="62" x2="266" y2="74" />
          <line x1="266" y1="62" x2="248" y2="74" />
          <line x1="284" y1="74" x2="302" y2="86" />
          <line x1="302" y1="74" x2="284" y2="86" />
        </g>
        {/* Lime connection rivets */}
        <g fill="#A6CE39" className="eb-epc-spark-pulse">
          <rect x="247" y="73" width="2" height="2" />
          <rect x="265" y="85" width="2" height="2" />
          <rect x="283" y="73" width="2" height="2" />
          <rect x="301" y="85" width="2" height="2" />
        </g>
      </g>

      {/* Layer 9 — engraved solar panel array under construction (foreground) */}
      <g>
        {/* 3 completed tilted panels + 1 in-progress (rightmost frame only) */}
        {[0, 1, 2, 3].map((i) => {
          const cx = 332 + i * 28;
          const incomplete = i === 3;
          return (
            <g key={i} transform={`translate(${cx} 92) rotate(-12)`}>
              {/* panel frame */}
              <rect x="-12" y="-5" width="24" height="10" fill="none" stroke="#006346" strokeOpacity={incomplete ? 0.45 : 0.75} strokeWidth="0.55" strokeDasharray={incomplete ? '1 1.2' : undefined} />
              {/* faint tint inside completed panels */}
              {!incomplete && (
                <rect x="-12" y="-5" width="24" height="10" fill="#52BF9D" fillOpacity="0.08" />
              )}
              {/* cells — 4×2 grid, skipped for incomplete */}
              {!incomplete && (
                <g stroke="#006346" strokeOpacity="0.45" strokeWidth="0.35">
                  <line x1="-6" y1="-5" x2="-6" y2="5" />
                  <line x1="0" y1="-5" x2="0" y2="5" />
                  <line x1="6" y1="-5" x2="6" y2="5" />
                  <line x1="-12" y1="0" x2="12" y2="0" />
                </g>
              )}
              {/* support leg */}
              <line x1="0" y1="5" x2="2" y2="12" stroke="#006346" strokeOpacity="0.6" strokeWidth="0.5" />
            </g>
          );
        })}
      </g>

      {/* Layer 10 — ground hatching across bottom */}
      <g stroke="#006346" strokeOpacity="0.35" strokeWidth="0.5" strokeLinecap="round">
        <line x1="20" y1="106" x2="450" y2="106" strokeDasharray="2 3" />
        <line x1="14" y1="109" x2="460" y2="109" strokeDasharray="1 2" />
        <line x1="24" y1="112" x2="446" y2="112" strokeDasharray="0.6 2" />
      </g>

      {/* Layer 11 — engraved safety cone (x=410 y=100) */}
      <g>
        {/* triangle outline */}
        <path d="M 410 100 L 414 88 L 418 100 Z" fill="none" stroke="#006346" strokeOpacity="0.75" strokeWidth="0.55" />
        {/* 2 horizontal stripes */}
        <line x1="411" y1="96" x2="417" y2="96" stroke="#A6CE39" strokeOpacity="0.85" strokeWidth="0.6" />
        <line x1="411.7" y1="93" x2="416.3" y2="93" stroke="#A6CE39" strokeOpacity="0.85" strokeWidth="0.5" />
        {/* base */}
        <rect x="408" y="100" width="12" height="1.5" fill="none" stroke="#006346" strokeOpacity="0.7" strokeWidth="0.5" />
      </g>

      {/* Layer 12 — engraved toolbox (x=150 y=98) */}
      <g>
        <rect x="142" y="100" width="16" height="6" fill="none" stroke="#006346" strokeOpacity="0.75" strokeWidth="0.55" />
        <line x1="142" y1="102" x2="158" y2="102" stroke="#006346" strokeOpacity="0.55" strokeWidth="0.4" />
        {/* handle */}
        <path d="M 146 100 Q 150 96 154 100" fill="none" stroke="#006346" strokeOpacity="0.7" strokeWidth="0.55" strokeLinecap="round" />
        {/* latch */}
        <rect x="149" y="103" width="2" height="1.2" fill="#A6CE39" fillOpacity="0.8" />
      </g>

      {/* Layer 13 — decorative right-edge engraved wind turbine */}
      <EngravedTurbine x={455} y={50} scale={0.5} bladeClass="eb-epc-blades-slow" opacity={0.7} />

      {/* Layer 14 — tiny lime sparkles */}
      <g fill="#A6CE39" className="eb-epc-spark-pulse" fillOpacity="0.7">
        <circle cx="118" cy="26" r="0.9" />
        <circle cx="232" cy="22" r="0.8" />
        <circle cx="318" cy="42" r="0.9" />
        <circle cx="396" cy="78" r="0.7" />
      </g>

      {/* Layer 15 — denomination-style serial corner marks */}
      <g fill="#006346" fillOpacity="0.35">
        <rect x="22" y="20" width="14" height="1" rx="0.5" />
        <rect x="22" y="22.5" width="9" height="0.8" rx="0.4" />
        <rect x="444" y="98" width="14" height="1" rx="0.5" />
        <rect x="449" y="100.5" width="9" height="0.8" rx="0.4" />
      </g>
    </svg>
  );
}
